import React, { memo, useCallback, type ReactNode } from 'react';
import Button from '@atlaskit/button';
import {
	useFeedbackCollectorActions,
	ENTRYPOINT_ID_JIRA_PLATFORM,
	EntryKeys,
} from '@atlassian/jira-feedback-collector';
import { useIntl } from '@atlassian/jira-intl';
import { useRouter } from '@atlassian/jira-router';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import messages from './messages';

export const FeedbackContactLabel = memo(() => {
	const { formatMessage } = useIntl();

	return (
		<p>
			{formatMessage(messages.canContactPrompt, {
				link: (content: ReactNode) => (
					<Button
						appearance="link"
						spacing="none"
						target="_blank"
						href="https://www.atlassian.com/legal/privacy-policy"
					>
						{content}
					</Button>
				),
			})}
		</p>
	);
});

export const useFeedbackCollector = () => {
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const { isAdmin, isSiteAdmin } = useTenantContext();
	const [
		{
			route: { name, path },
		},
	] = useRouter();
	const { formatMessage } = useIntl();

	return useCallback(
		({
			feedbackGroupId,
			onSubmit,
			feedbackTitle,
			customTextAreaLabel,
		}: {
			feedbackGroupId: string;
			onSubmit?: () => void;
			feedbackTitle?: string;
			customTextAreaLabel?: string;
		}) => {
			openFeedbackCollector({
				entrypointId: ENTRYPOINT_ID_JIRA_PLATFORM,
				feedbackGroupId,
				feedbackTitle,
				showTypeField: false,
				showDefaultTextFields: true,
				canBeContactedLabel: <FeedbackContactLabel />,
				submitButtonLabel: formatMessage(messages.submitButtonLabel),
				// @ts-expect-error: Object literal may only specify known properties, and 'customTextAreaLabel' does not exist in type 'FeedbackCollectorProps'.ts(2345)
				customTextAreaLabel,
				onSubmit,
				feedbackContext: [
					{
						id: EntryKeys.INLINE_FEEDBACK_ID,
						value: feedbackGroupId,
					},
					{
						id: EntryKeys.ROUTE_NAME,
						value: name,
					},
					{
						id: EntryKeys.IS_ADMIN,
						value: isAdmin,
					},
					{
						id: EntryKeys.IS_SITE_ADMIN,
						value: isSiteAdmin,
					},
					{
						id: EntryKeys.LOCATION,
						value: path,
					},
				],
			});
		},
		[formatMessage, isAdmin, isSiteAdmin, name, openFeedbackCollector, path],
	);
};
